import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes as RouterRoutes, useNavigate, useLocation } from "react-router-dom";
import FeedContainer from "../feed";
import { PaymentError } from "../../components/common/paymentError";
import Layout from "../../components/layout";
import RouteFallback from "../../components/common/routeFallback";

/* hide due this page https://heritageweb.atlassian.net/browse/GROWTH-2086
import CannyContainer from "../canny";
*/

import { ImportBanner } from "../../components/importBanner";
import { Importing } from "../../components/importing";
import { ImportingSuccess } from "../../components/importingSuccess";
import VerificationEmailFlow from "../../components/verificationEmailFlow";
import { AddNewListing } from "../../components/listings/components/modals/addFreeListing";
import { getQueryParams } from "../../helpers";
import { buildTeamSelection, findActiveMemberAccess } from "../../helpers/teamPermissions";
import { actions as authActions } from "../../actions/auth";
import { actions as teamActions } from "../../actions/teams";
import { actions as profileActions } from "../../actions/account/profile";
import { actions as mainActions } from "../../actions/main";

import { lazyWithRetry } from "../../helpers/lazyWithRetry";
import { logoutOfKeycloak } from "../../helpers/keycloakLogout";
import { PAID_REFERRALS_V2, PRO_BONO_V2 } from "../../constants/featureFlags";
import RedirectKeepingQuery from "../../components/payPerLead/redirectKeepingQuery";

const SavedContainer = lazyWithRetry(() => import("../saved"));
const FollowingContainer = lazyWithRetry(() => import("../following"));
const CallHistoryContainer = lazyWithRetry(() => import("../callHistory"));
const NotificationsContainer = lazyWithRetry(() => import("../notifications"));
const Payments = lazyWithRetry(() => import("../payments"));
const ManageNotification = lazyWithRetry(() => import("../manageNotification"));
const TeamsContainer = lazyWithRetry(() => import("../teams"));
const PayPerLeadContainer = lazyWithRetry(() => import("../payPerLead"));
const ProBonoContainer = lazyWithRetry(() => import("../proBono"));
const ProBonoDetailContainer = lazyWithRetry(() => import("../proBonoDetail"));
const ReferralsContainer = lazyWithRetry(() => import("../referrals"));
const PayPerLeadDetailContainer = lazyWithRetry(() => import("../payPerLeadDetail"));
const PerformanceContainer = lazyWithRetry(() => import("../performance"));
const Account = lazyWithRetry(() => import("../account"));
const PartnersContainer = lazyWithRetry(() => import("../partners"));
const ListingsContainer = lazyWithRetry(() => import("../listings"));
const ListingDetailContainer = lazyWithRetry(() => import("../listingDetail"));
const NotFoundContainer = lazyWithRetry(() => import("../404"));
const QuestionsAnswersContainer = lazyWithRetry(() => import("../questionsAnswers"));
const QuestionContainer = lazyWithRetry(() => import("../question"));
const ReferralRequestDetailContainer = lazyWithRetry(() => import("../referralRequestDetail"));
const Messages = lazyWithRetry(() => import("../messages"));
const CommunityReferralsContainer = lazyWithRetry(() => import("../communityReferrals"));
const CommunityReferralDetailContainer = lazyWithRetry(() => import("../communityReferralDetail"));

const PrivateRoutes = ({ keycloak }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { client, countries, states, isActiveNewListing, listingModalListingType, footerInfo, socialLinks } = useSelector(
    state => state.main
  );
  const {
    listings_count: listingsCount,
    referral_requests_pending_import: referralRequestsPendingImport,
    referral_requests_count: referralRequestsCount,
    new_messages: newMessages,
    unread_messages: unreadMessages,
    email
  } = useSelector(state => state.account.profile);

  const { importReady, modals: modalsReferralRequest } = useSelector(state => state.referralRequests);
  const { currentTeam, member, memberLoaded } = useSelector(state => state.teams);

  const queryParams = useMemo(() => getQueryParams(window.location.search), [window.location.search]);
  const setLoggedIn = useCallback(isActive => dispatch(authActions.setLoggedIn(isActive)), [dispatch]);
  const getProfile = useCallback(() => dispatch(profileActions.getProfile()), [dispatch]);
  const toggleNewListing = useCallback(isActive => dispatch(mainActions.toggleNewListing(isActive)), [dispatch]);
  const setCurrentTeam = useCallback(team => dispatch(teamActions.setCurrentTeam(team)), [dispatch]);
  const getTeamsMember = useCallback(() => dispatch(teamActions.getTeamsMember()), [dispatch]);

  const isActivePath = useMemo(() => {
    const { pathname } = location;
    return {
      isMessages: pathname === "/inbox",
      isHome: pathname === "/",
      isQA: pathname.includes("question-answer"),
      isReferralRequests: pathname.includes("referral-requests"),
      isPaidLeads: pathname.includes("paid-leads") || pathname.includes("paid-referrals")
    };
  }, [location.pathname]);

  const { isMessages, isHome, isQA, isReferralRequests, isPaidLeads } = isActivePath;

  const checkEmptyCounts = counts => Object.values(counts).every(value => String(value) === "0");

  const isShowOnlyListingBanner = useMemo(
    () =>
      checkEmptyCounts({
        unreadMessages,
        listingsCount,
        referralRequestsPendingImport,
        referralRequestsCount,
        newMessages
      }),
    [unreadMessages, listingsCount, referralRequestsPendingImport, referralRequestsCount, newMessages]
  );

  const isShowFooter = useMemo(() => {
    return (isHome && isShowOnlyListingBanner) || isMessages;
  }, [isHome, isShowOnlyListingBanner, isMessages]);

  const isShowBannerReferralRequests = useMemo(() => {
    return isMessages || isQA || isReferralRequests || isPaidLeads;
  }, [isMessages, isQA, isReferralRequests, isPaidLeads]);

  const closeNewListing = () => {
    toggleNewListing(false);
  };

  const getCountriesAndStates = useCallback(() => dispatch(mainActions.getCountriesAndStates()), [dispatch]);

  useLayoutEffect(() => {
    if (!countries?.length) {
      if (!email) {
        getProfile();
      } else {
        getCountriesAndStates();
      }
    }
  }, [getProfile, getCountriesAndStates, countries?.length, email]);

  useEffect(() => {
    getCountriesAndStates();
  }, [getCountriesAndStates]);

  useEffect(() => {
    if (queryParams?.isAuthenticationError) {
      setLoggedIn(false);
      logoutOfKeycloak(keycloak, { redirectUri: `${window.location.origin}/login?isAuthenticationError=true` }).then(() => {
        navigate("/login");
      });
    }
  }, [queryParams.isAuthenticationError, setLoggedIn, navigate, keycloak]);

  useEffect(() => {
    if (queryParams.user_id) {
      getTeamsMember();
    }
  }, [getTeamsMember, queryParams.user_id]);

  // Always keep the selected team owner from URL in Redux on refresh.
  // If access details aren't loaded yet (or fail), we keep role as `null` (pending),
  // so the UI stays view-only but the correct account data is still requested via `user_id`.
  useEffect(() => {
    if (!queryParams.user_id) {
      return;
    }

    const requestedOwnerId = String(queryParams.user_id);
    if (String(currentTeam?.value) === requestedOwnerId) {
      return;
    }

    setCurrentTeam({ value: requestedOwnerId, label: "", role: null });
  }, [queryParams.user_id, setCurrentTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!queryParams.user_id) {
      return;
    }
    if (!memberLoaded) {
      return;
    }

    if (!member?.length) {
      return;
    }

    const access = findActiveMemberAccess(member, queryParams.user_id);
    if (!access) {
      return;
    }

    const selection = buildTeamSelection(access);
    if (String(currentTeam.value) !== selection.value || Number(currentTeam.role) !== selection.role) {
      setCurrentTeam(selection);
    }
  }, [
    setCurrentTeam,
    queryParams.user_id,
    member,
    memberLoaded,
    currentTeam.value,
    currentTeam.role,
    currentTeam?.value,
    location.pathname,
    location.search,
    location.hash,
    navigate
  ]);

  return (
    <Layout keycloak={keycloak} isShowFooter={isShowFooter} footerInfo={footerInfo} socialLinks={socialLinks}>
      {isShowBannerReferralRequests ? <ImportBanner importReady={importReady} /> : null}

      <VerificationEmailFlow />

      {modalsReferralRequest.importing ? <Importing /> : null}
      {modalsReferralRequest.importingSuccess ? <ImportingSuccess /> : null}
      <Suspense fallback={<RouteFallback />}>
        <RouterRoutes>
          <Route path="/" element={<FeedContainer />} />
          <Route path="/following" element={<FollowingContainer />} />
          <Route path="/saved" element={<SavedContainer />} />
          <Route path="/inbox" element={<Messages />} />
          <Route path="/listings" element={<ListingsContainer countries={countries} />} />
          <Route path="/my-events" element={<ListingsContainer countries={countries} />} />
          <Route path="/my-jobs" element={<ListingsContainer countries={countries} />} />
          <Route path="/my-organizations" element={<ListingsContainer countries={countries} />} />
          <Route path="/call-history" element={<CallHistoryContainer />} />
          {PAID_REFERRALS_V2 ? (
            <>
              <Route path="/paid-referrals" element={<PayPerLeadContainer />} />
              <Route path="/paid-leads" element={<RedirectKeepingQuery to="/paid-referrals" />} />
            </>
          ) : (
            <Route path="/paid-leads" element={<PayPerLeadContainer />} />
          )}
          {PRO_BONO_V2 ? (
            <>
              <Route path="/pro-bono-referrals" element={<ProBonoContainer />} />
              <Route path="/pro-bono-referrals/:id" element={<ProBonoDetailContainer />} />
              <Route path="/pro-bono" element={<RedirectKeepingQuery to="/pro-bono-referrals" />} />
              <Route path="/pro-bono/:id" element={<RedirectKeepingQuery to="/pro-bono-referrals" withId />} />
            </>
          ) : (
            <>
              <Route path="/pro-bono" element={<ProBonoContainer />} />
              <Route path="/pro-bono/:id" element={<ProBonoDetailContainer />} />
            </>
          )}
          <Route path="/referrals" element={<ReferralsContainer />} />
          <Route path="/community-referrals" element={<CommunityReferralsContainer />} />
          <Route path="/community-referrals/:id" element={<CommunityReferralDetailContainer />} />
          {PAID_REFERRALS_V2 ? (
            <>
              <Route path="/paid-referrals/:id" element={<PayPerLeadDetailContainer />} />
              <Route path="/paid-leads/:id" element={<RedirectKeepingQuery to="/paid-referrals" withId />} />
            </>
          ) : (
            <Route path="/paid-leads/:id" element={<PayPerLeadDetailContainer />} />
          )}
          <Route path="/notifications" element={<NotificationsContainer />} />
          <Route path="/manage-notification" element={<ManageNotification />} />
          <Route path="/account" element={<Account keycloak={keycloak} countries={countries} />} />
          <Route path="/affiliate" element={<PartnersContainer />} />
          <Route path="/partners" element={<Navigate to="/affiliate" replace />} />
          <Route path="/payments" element={<Payments countries={countries} keycloak={keycloak} />} />
          <Route path="/teams" element={<TeamsContainer />} />
          {/* My Access moved into the Team page (GROWTH-7703); the redirect keeps bookmarks and the invitation email's link working. */}
          <Route path="/access" element={<Navigate to="/teams" replace />} />
          <Route path="/question-answer" element={<QuestionsAnswersContainer />} />
          <Route path="/question-answer/:id" element={<QuestionContainer />} />
          <Route path="/analytics" element={<PerformanceContainer />} />
          <Route path="/referral-requests/:id" element={<ReferralRequestDetailContainer client={client} />} />
          <Route path="/profile/*" element={<ListingDetailContainer utilityCountries={countries ?? []} utilityStates={states ?? []} />} />
          <Route
            path="/listings/:id/*"
            element={<ListingDetailContainer utilityCountries={countries ?? []} utilityStates={states ?? []} />}
          />
          <Route
            path="/my-events/:id/*"
            element={<ListingDetailContainer utilityCountries={countries ?? []} utilityStates={states ?? []} />}
          />
          <Route
            path="/my-jobs/:id/*"
            element={<ListingDetailContainer utilityCountries={countries ?? []} utilityStates={states ?? []} />}
          />
          <Route
            path="/my-organizations/:id/*"
            element={<ListingDetailContainer utilityCountries={countries ?? []} utilityStates={states ?? []} />}
          />
          {/* hide due this page https://heritageweb.atlassian.net/browse/GROWTH-2086
                <Route exact path="/product-roadmap" render={() => <CannyContainer />} />
              */}
          <Route path="/404" element={<NotFoundContainer />} />
          <Route path="*" element={<NotFoundContainer />} />
        </RouterRoutes>
      </Suspense>
      {isActiveNewListing ? (
        <AddNewListing listingModalListingType={listingModalListingType} closeModal={closeNewListing} clearAllModals={closeNewListing} />
      ) : null}
      {/* Keep this last. The toast and the Add Card / Edit Card modals (`zIndex4`) share layer 4, and on a tie the later element in
          the page wins, so rendered before the routes the toast was painted under their backdrop (GROWTH-7378). */}
      <PaymentError />
    </Layout>
  );
};

export default PrivateRoutes;
